// UltraFlex Admin — Payroll Dashboard (Weekly Two-Step)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Coins, Check, ChevronLeft, ChevronRight, Download, RotateCcw, Send, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPayroll() {
  const { token } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodDetail, setPeriodDetail] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const getWeekStart = (offset = 0) => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1 + offset * 7); // Monday
    return d.toISOString().split('T')[0];
  };

  useEffect(() => { fetchDashboard(); }, [weekOffset]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const ws = getWeekStart(weekOffset);
      const res = await fetch(`${API}/api/payroll/dashboard?week_start=${ws}`, { headers });
      setDashboard(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const openPeriod = async (siteId) => {
    const ws = getWeekStart(weekOffset);
    const res = await fetch(`${API}/api/payroll/periods`, { method: 'POST', headers, body: JSON.stringify({ site_id: siteId, week_start: ws }) });
    const period = await res.json();
    setSelectedPeriod(period.id);
    fetchPeriodDetail(period.id);
  };

  const fetchPeriodDetail = async (periodId) => {
    const res = await fetch(`${API}/api/payroll/periods/${periodId}`, { headers });
    setPeriodDetail(await res.json());
  };

  const signOff = async (periodId) => {
    await fetch(`${API}/api/payroll/periods/${periodId}/sign-off`, { method: 'POST', headers });
    fetchPeriodDetail(periodId);
    fetchDashboard();
  };

  const authorise = async (periodId) => {
    await fetch(`${API}/api/payroll/periods/${periodId}/authorise`, { method: 'POST', headers });
    fetchPeriodDetail(periodId);
    fetchDashboard();
  };

  const reopen = async (periodId) => {
    await fetch(`${API}/api/payroll/periods/${periodId}/reopen`, { method: 'POST', headers });
    fetchPeriodDetail(periodId);
    fetchDashboard();
  };

  const exportPeriod = async (periodId) => {
    const res = await fetch(`${API}/api/payroll/periods/${periodId}/export`, { headers });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `payroll_${periodId.slice(0, 8)}.xlsx`; a.click();
    fetchDashboard();
  };

  const exportAll = async () => {
    const ws = getWeekStart(weekOffset);
    const res = await fetch(`${API}/api/payroll/export-all?week_start=${ws}`, { headers });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `payroll_all_${ws}.xlsx`; a.click();
  };

  const StatusBadge = ({ status }) => {
    const m = { open: 'bg-blue-900/40 text-blue-400', signed_off: 'bg-yellow-900/40 text-yellow-400', authorised: 'bg-green-900/40 text-green-400', exported: 'bg-purple-900/40 text-purple-400', no_period: 'bg-gray-800 text-gray-500' };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${m[status] || 'bg-gray-800 text-gray-400'}`}>{(status || '').replace('_', ' ')}</span>;
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Payroll</h1>
          <p className="text-sm text-gray-400 mt-1">Weekly two-step approval: Manager sign-off then Accounts authorise</p>
        </div>
        <button onClick={exportAll} data-testid="export-all-btn"
          className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 flex items-center gap-2">
          <Download className="w-4 h-4" /> Export All Sites
        </button>
      </div>

      {/* Week navigation */}
      <div className="flex items-center gap-4 bg-[#111] border border-gray-800 rounded-xl p-4">
        <button onClick={() => setWeekOffset(w => w - 1)} className="text-gray-400 hover:text-white"><ChevronLeft className="w-5 h-5" /></button>
        <div className="text-white font-medium">{dashboard?.week_start} — {dashboard?.week_end}</div>
        <button onClick={() => setWeekOffset(w => w + 1)} className="text-gray-400 hover:text-white"><ChevronRight className="w-5 h-5" /></button>
        <button onClick={() => setWeekOffset(0)} className="text-xs text-gray-500 hover:text-white ml-2">This Week</button>
        {dashboard?.all_signed_off && <span className="ml-auto text-xs text-green-400 flex items-center gap-1"><Check className="w-3 h-3" />All Signed Off</span>}
      </div>

      {loading ? <div className="text-gray-500 text-center py-8">Loading...</div> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Site sign-off grid */}
          <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
            <div className="p-3 border-b border-gray-800"><h2 className="text-sm font-medium text-gray-400 uppercase">Sign-off Status by Site</h2></div>
            <div className="divide-y divide-gray-800/50">
              {(dashboard?.sites || []).map(s => (
                <div key={s.site_id} className="p-3 flex items-center justify-between hover:bg-gray-900/50 cursor-pointer" onClick={() => s.period_id ? fetchPeriodDetail(s.period_id) && setSelectedPeriod(s.period_id) : openPeriod(s.site_id)}>
                  <div>
                    <div className="text-white text-sm">{s.site_name} <span className="text-gray-600 text-xs">({s.site_code})</span></div>
                    <div className="text-xs text-gray-500">{s.entry_count} staff · {s.total_hours}h · £{s.total_pay?.toFixed(2)}</div>
                    {s.signed_off_by && <div className="text-xs text-gray-600">Signed by: {s.signed_off_by}</div>}
                  </div>
                  <StatusBadge status={s.status} />
                </div>
              ))}
            </div>
          </div>

          {/* Period detail */}
          <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
            {periodDetail ? (
              <>
                <div className="p-3 border-b border-gray-800 flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-medium text-white">{periodDetail.site_name}</h2>
                    <div className="text-xs text-gray-500">{periodDetail.week_start} — {periodDetail.week_end}</div>
                  </div>
                  <div className="flex gap-2">
                    {periodDetail.status === 'open' && (
                      <button onClick={() => signOff(periodDetail.id)} data-testid="sign-off-btn"
                        className="px-3 py-1.5 bg-yellow-600 text-white rounded text-xs hover:bg-yellow-700 flex items-center gap-1">
                        <Check className="w-3 h-3" /> Sign Off
                      </button>
                    )}
                    {periodDetail.status === 'signed_off' && (
                      <button onClick={() => authorise(periodDetail.id)} data-testid="authorise-btn"
                        className="px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 flex items-center gap-1">
                        <Check className="w-3 h-3" /> Authorise
                      </button>
                    )}
                    {periodDetail.status === 'authorised' && (
                      <button onClick={() => exportPeriod(periodDetail.id)} data-testid="export-btn"
                        className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 flex items-center gap-1">
                        <Download className="w-3 h-3" /> Export
                      </button>
                    )}
                    {['signed_off', 'authorised'].includes(periodDetail.status) && (
                      <button onClick={() => reopen(periodDetail.id)} className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded text-xs hover:bg-gray-600 flex items-center gap-1">
                        <RotateCcw className="w-3 h-3" /> Reopen
                      </button>
                    )}
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                    <th className="text-left p-3">Employee</th><th className="text-right p-3">Hours</th>
                    <th className="text-right p-3">Rate</th><th className="text-right p-3">Gross</th>
                  </tr></thead>
                  <tbody>
                    {(periodDetail.entries || []).map(e => (
                      <tr key={e.id} className="border-b border-gray-800/50">
                        <td className="p-3 text-white text-sm">{e.staff_name} <span className="text-gray-600 text-xs">{e.employee_number}</span></td>
                        <td className="p-3 text-right text-gray-300">{e.hours_worked}</td>
                        <td className="p-3 text-right text-gray-400">£{e.hourly_rate?.toFixed(2)}</td>
                        <td className="p-3 text-right text-white font-medium">£{e.gross_pay?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <div className="p-8 text-center text-gray-600">
                <Coins className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Select a site to view payroll detail</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
